"use client";

import { useState, useMemo, type ReactNode } from "react";
import { useTranslations } from "use-intl";

import { Link } from "@/components/link";
import { cn } from "@/lib/utils";

interface BioTextProps {
  text: string;
  maxLines?: number;
  className?: string;
}

// AST node types for the BBCode + reference parser
type ASTNode =
  | { type: "text"; content: string }
  | { type: "bold"; children: ASTNode[] }
  | { type: "italic"; children: ASTNode[] }
  | { type: "underline"; children: ASTNode[] }
  | { type: "url"; href: string; children: ASTNode[] }
  | { type: "ref"; entityType: string; id: string; name: string };

/**
 * Map a Discogs entity URL to the equivalent local Garimpa path.
 * Returns null if the URL is not a recognized Discogs entity URL.
 */
function rewriteDiscogsUrl(href: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(href);
  } catch {
    return null;
  }
  if (!/(^|\.)discogs\.com$/.test(parsed.hostname)) return null;
  // Optional 2-3 letter locale prefix (e.g. /de/, /pt/, /es/).
  const match = parsed.pathname.match(
    /^\/(?:[a-z]{2,3}\/)?(release|master|artist|label)\/(\d+)(?:[-/].*)?$/,
  );
  if (!match) return null;
  const [, entity, id] = match;
  switch (entity) {
    case "release":
      return `/release/${id}`;
    case "master":
      return `/work/${id}`;
    case "artist":
      return `/artist/${id}`;
    case "label":
      return `/company/${id}`;
    default:
      return null;
  }
}

const OPEN_TAG_RE = /\[(b|i|u|url(?:=[^\]]*)?)\]/;
const REF_RE = /\{\{(artist|album|work|company|label|master|release):(\d+):([^}]+)\}\}/;
// Discogs raw notation: [a123], [a=Name], [l123], [l=Name], [m123], [r123], [m=Name]
const DISCOGS_RE = /\[([almr])(?:=([^\]]+)|(\d+))\]/;

type TokenKind = "tag" | "ref" | "discogs";

interface ParserToken {
  index: number;
  kind: TokenKind;
  match: RegExpMatchArray;
}

function tokenCandidate(match: RegExpMatchArray | null, kind: TokenKind): ParserToken | null {
  if (!match || match.index === undefined) return null;
  return { index: match.index, match, kind };
}

function earlierToken(left: ParserToken | null, right: ParserToken | null): ParserToken | null {
  if (!left) return right;
  if (!right) return left;
  return right.index < left.index ? right : left;
}

/**
 * Find the next BBCode tag, {{reference}} placeholder, or Discogs notation in the text.
 */
function findNextToken(text: string) {
  const tag = tokenCandidate(text.match(OPEN_TAG_RE), "tag");
  const reference = tokenCandidate(text.match(REF_RE), "ref");
  const discogs = tokenCandidate(text.match(DISCOGS_RE), "discogs");
  return earlierToken(earlierToken(tag, reference), discogs);
}

interface ParsedToken {
  consumed: number;
  node: ASTNode | null;
}

function parseReferenceToken(token: ParserToken): ParsedToken {
  const [fullMatch, entityType, id, name] = token.match;
  return {
    consumed: token.index + fullMatch!.length,
    node: { type: "ref", entityType: entityType!, id: id!, name: name! },
  };
}

function parseDiscogsToken(token: ParserToken): ParsedToken {
  const [fullMatch, typeChar, name, id] = token.match;
  const entityType = { a: "artist", l: "company", m: "work", r: "release" }[typeChar!] ?? "company";
  const consumed = token.index + fullMatch!.length;
  if (name) return { consumed, node: { type: "ref", entityType, id: "0", name } };
  if (id) return { consumed, node: { type: "ref", entityType, id, name: "" } };
  return { consumed, node: null };
}

function formattedNode(tagName: string, href: string, children: ASTNode[]): ASTNode | null {
  switch (tagName) {
    case "b":
      return { type: "bold", children };
    case "i":
      return { type: "italic", children };
    case "u":
      return { type: "underline", children };
    case "url":
      return { type: "url", href, children };
    default:
      return null;
  }
}

function parseTagToken(token: ParserToken, remaining: string): ParsedToken {
  const fullTag = token.match[1]!;
  const tagName = fullTag.startsWith("url") ? "url" : fullTag;
  const href = fullTag.startsWith("url=") ? fullTag.slice(4) : "";
  const closeTag = `[/${tagName}]`;
  const afterOpen = token.index + token.match[0]!.length;
  const closeIndex = remaining.indexOf(closeTag, afterOpen);

  if (closeIndex === -1) {
    return {
      consumed: afterOpen,
      node: { type: "text", content: token.match[0]! },
    };
  }

  return {
    consumed: closeIndex + closeTag.length,
    node: formattedNode(tagName, href, parse(remaining.slice(afterOpen, closeIndex))),
  };
}

function parseToken(token: ParserToken, remaining: string): ParsedToken {
  if (token.kind === "ref") return parseReferenceToken(token);
  if (token.kind === "discogs") return parseDiscogsToken(token);
  return parseTagToken(token, remaining);
}

/**
 * Parse text containing BBCode tags ([b], [i], [u], [url=...]) and
 * Discogs reference placeholders ({{artist:id:name}}) into an AST.
 */
function parse(text: string): ASTNode[] {
  const nodes: ASTNode[] = [];
  let remaining = text;

  while (remaining.length > 0) {
    const token = findNextToken(remaining);

    if (!token) {
      nodes.push({ type: "text", content: remaining });
      break;
    }

    if (token.index > 0) {
      nodes.push({ type: "text", content: remaining.slice(0, token.index) });
    }

    const parsed = parseToken(token, remaining);
    if (parsed.node) nodes.push(parsed.node);
    remaining = remaining.slice(parsed.consumed);
  }

  return nodes;
}

/**
 * Render AST nodes to React elements.
 */
function renderNodes(nodes: ASTNode[], keyPrefix = ""): ReactNode[] {
  return nodes.map((node, i) => {
    const key = `${keyPrefix}${i}`;
    switch (node.type) {
      case "text":
        return node.content;
      case "bold":
        return <strong key={key}>{renderNodes(node.children, `${key}-`)}</strong>;
      case "italic":
        return <em key={key}>{renderNodes(node.children, `${key}-`)}</em>;
      case "underline":
        return (
          <span key={key} className="underline">
            {renderNodes(node.children, `${key}-`)}
          </span>
        );
      case "url": {
        const localHref = rewriteDiscogsUrl(node.href);
        if (localHref) {
          return (
            <Link key={key} to={localHref} className="text-primary hover:underline font-medium">
              {renderNodes(node.children, `${key}-`)}
            </Link>
          );
        }
        return (
          <a
            key={key}
            href={node.href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            {renderNodes(node.children, `${key}-`)}
          </a>
        );
      }
      case "ref": {
        // ID-only refs without a name — skip rendering (no useful display text)
        if (!node.name) return null;

        // Name-only refs (id "0") — render as styled text, no link
        if (node.id === "0") {
          return (
            <span key={key} className="text-foreground font-medium">
              {node.name}
            </span>
          );
        }

        // Linkable entity refs
        const hrefMap: Record<string, string> = {
          artist: `/artist/${node.id}`,
          company: `/company/${node.id}`,
          label: `/company/${node.id}`,
          album: `/work/${node.id}`,
          work: `/work/${node.id}`,
          master: `/work/${node.id}`,
          release: `/release/${node.id}`,
        };
        const href = hrefMap[node.entityType];
        if (href) {
          return (
            <Link key={key} to={href} className="text-primary hover:underline font-medium">
              {node.name}
            </Link>
          );
        }

        return (
          <span key={key} className="text-foreground font-medium">
            {node.name}
          </span>
        );
      }
    }
  });
}

export function BioText({ text, maxLines = 0, className }: BioTextProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const t = useTranslations("shared.bioText");

  const parsedContent = useMemo(() => renderNodes(parse(text)), [text]);

  // Estimate if text is long enough to need truncation (roughly 100 chars per line)
  const needsTruncation = maxLines > 0 && text.length > maxLines * 100;
  const wrapperClass = cn(
    "text-justify text-muted-foreground whitespace-pre-line hyphens-auto md:text-left",
    className,
  );

  if (!needsTruncation || isExpanded) {
    return (
      <div className={wrapperClass}>
        <p>{parsedContent}</p>
        {needsTruncation && (
          <button
            onClick={() => setIsExpanded(false)}
            className="mt-2 inline-flex min-h-11 items-center rounded-lg px-2 text-sm font-semibold text-primary hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            {t("showLess")}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
      <p
        style={{
          display: "-webkit-box",
          WebkitLineClamp: maxLines,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {parsedContent}
      </p>
      <button
        onClick={() => setIsExpanded(true)}
        className="mt-2 inline-flex min-h-11 items-center rounded-lg px-2 text-sm font-semibold text-primary hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      >
        {t("showMore")}
      </button>
    </div>
  );
}
